import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Paper, Typography, Chip, CircularProgress,
  List, ListItem, ListItemText, Divider, Link, Grid, Accordion,
  AccordionSummary, AccordionDetails, Badge, Checkbox, Collapse,
  ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select,
  MenuItem, Autocomplete, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { searchQuestions } from '../services/api';
import { useSearchParams } from 'react-router-dom';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';
import ExportCSVButton from './ExportCSVButton';
import { sortFacetsBooleansFirst } from '../utils/facetOrder';

export default function SimpleSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchMode, setSearchMode] = useState(searchParams.get('mode') || 'simple');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedMetadata, setExpandedMetadata] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });

  // Advanced form state
  const [form, setForm] = useState({
    keywords: '',
    senator: '',
    committee: '',
    portfolio: '',
    agency: '',
    estimatesRound: '',
    hearingDateFrom: '',
    hearingDateTo: '',
    questionNumber: '',
    questionStatus: 'all',
    portfolioQuestionNumber: ''
  });

  // Available options for autocomplete
  const [availableOptions, setAvailableOptions] = useState({
    senators: [],
    committees: [],
    portfolios: [],
    agencies: [],
    estimatesRounds: []
  });

  const examples = [
    'senator:"Jane Hume"',
    'portfolio:"Health, Disability and Ageing" AND status:answered',
    'senator:"Sarah Henderson" AND committee:"Environment and Communications"',
    'hearing_date:07/10/2025',
    'portfolio:Treasury AND status:unanswered'
  ];

  // Execute search from URL on component mount, or default search
  useEffect(() => {
    const urlQuery = searchParams.get('q');
    const urlFilters = searchParams.get('filters');

    if (urlQuery) {
      const parsedFilters = urlFilters ? JSON.parse(urlFilters) : {};
      setActiveFilters(parsedFilters);
      handleSearch(urlQuery, 1, parsedFilters);
    } else {
      // Perform default search on load
      handleSearch('*', 1, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Extract options from facets when results change
  useEffect(() => {
    if (results?.facets) {
      const newOptions = { ...availableOptions };
      if (results.facets.senators) {
        const senators = results.facets.senators.map(f => f.value).filter(Boolean).sort();
        if (senators.length > 0) newOptions.senators = [...new Set([...newOptions.senators, ...senators])].sort();
      }
      if (results.facets.committees) {
        const committees = results.facets.committees.map(f => f.value).filter(Boolean).sort();
        if (committees.length > 0) newOptions.committees = [...new Set([...newOptions.committees, ...committees])].sort();
      }
      if (results.facets.portfolios) {
        const portfolios = results.facets.portfolios.map(f => f.value).filter(Boolean).sort();
        if (portfolios.length > 0) newOptions.portfolios = [...new Set([...newOptions.portfolios, ...portfolios])].sort();
      }
      if (results.facets.agencies) {
        const agencies = results.facets.agencies.map(f => f.value).filter(Boolean).sort();
        if (agencies.length > 0) newOptions.agencies = [...new Set([...newOptions.agencies, ...agencies])].sort();
      }
      if (results.facets.estimates_rounds) {
        const estimatesRounds = results.facets.estimates_rounds.map(f => f.value).filter(Boolean).sort();
        if (estimatesRounds.length > 0) newOptions.estimatesRounds = [...new Set([...newOptions.estimatesRounds, ...estimatesRounds])].sort();
      }
      setAvailableOptions(newOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm({ ...form, [field]: value });
  };

  const handleAutocompleteChange = (field) => (event, newValue) => {
    setForm({ ...form, [field]: newValue || '' });
  };

  const buildAdvancedQuery = () => {
    const parts = [];

    if (form.keywords) parts.push(`(${form.keywords})`);
    if (form.senator) parts.push(`senator:"${form.senator}"`);
    if (form.committee) parts.push(`committee:"${form.committee}"`);
    if (form.portfolio) parts.push(`portfolio:"${form.portfolio}"`);
    if (form.agency) parts.push(`agency:"${form.agency}"`);
    if (form.estimatesRound) parts.push(`estimates_round:"${form.estimatesRound}"`);
    if (form.questionNumber) parts.push(`question_number:${form.questionNumber}`);
    if (form.portfolioQuestionNumber) parts.push(`portfolio_question_number:${form.portfolioQuestionNumber}`);
    if (form.questionStatus && form.questionStatus !== 'all') {
      parts.push(`status:${form.questionStatus}`);
    }
    if (form.hearingDateFrom && form.hearingDateTo) {
      parts.push(`hearing_date:[${form.hearingDateFrom} TO ${form.hearingDateTo}]`);
    }

    return parts.length > 0 ? parts.join(' AND ') : '*';
  };

  const buildQueryWithFilters = (baseQuery, filters) => {
    const filterParts = [];
    Object.entries(filters).forEach(([field, values]) => {
      if (values.length > 0) {
        const fieldQuery = values.map(v => `${field}:"${v}"`).join(' OR ');
        filterParts.push(`(${fieldQuery})`);
      }
    });

    if (filterParts.length === 0) {
      return String(baseQuery || '*');
    }

    const filterQuery = filterParts.join(' AND ');
    if (baseQuery && baseQuery !== '*') {
      return `(${baseQuery}) AND ${filterQuery}`;
    }
    return filterQuery;
  };

  const handleSearch = async (searchQuery = null, newPage = 1, filters = null, pageSize = perPage) => {
    let baseQuery;
    if (searchMode === 'simple') {
      baseQuery = typeof searchQuery === 'string' ? searchQuery : String(query || '*');
    } else {
      baseQuery = buildAdvancedQuery();
    }

    const filtersToUse = filters !== null && typeof filters === 'object' && !filters.nativeEvent ? filters : activeFilters;
    const fullQuery = buildQueryWithFilters(baseQuery, filtersToUse);

    if (!fullQuery || fullQuery.trim() === '') {
      return;
    }

    const urlParams = { q: baseQuery, mode: searchMode };
    if (Object.keys(filtersToUse).length > 0) {
      urlParams.filters = JSON.stringify(filtersToUse);
    }
    if (pageSize !== DEFAULT_PAGE_SIZE) urlParams.pageSize = pageSize;
    setSearchParams(urlParams);

    setLoading(true);
    try {
      const data = await searchQuestions(fullQuery, newPage, pageSize);
      setResults(data);
      setPage(newPage);
    } catch (error) {
      console.error('Search error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Search failed. Check console for details.';
      alert(`Search failed: ${errorMsg}`);
    }
    setLoading(false);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, activeFilters, newSize);
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage, activeFilters);
  };

  const handleFacetClick = (field, value) => {
    const newFilters = { ...activeFilters };
    if (!newFilters[field]) {
      newFilters[field] = [];
    }

    const valueIndex = newFilters[field].indexOf(value);
    if (valueIndex > -1) {
      newFilters[field] = newFilters[field].filter(v => v !== value);
      if (newFilters[field].length === 0) {
        delete newFilters[field];
      }
    } else {
      newFilters[field].push(value);
    }

    setActiveFilters(newFilters);
    handleSearch(null, 1, newFilters);
  };

  const removeFilter = (field, value) => {
    handleFacetClick(field, value);
  };

  const clearAllFilters = () => {
    setActiveFilters({});
    handleSearch(null, 1, {});
  };

  const isFilterActive = (field, value) => {
    return activeFilters[field]?.includes(value) || false;
  };

  const facetToField = (facetName) => {
    const mapping = {
      'question_types': 'question_type',
      'senators': 'senator',
      'committees': 'committee',
      'portfolios': 'portfolio',
      'agencies': 'asked_of',
      'statuses': 'status',
      'estimates_rounds': 'estimates_round'
    };
    return mapping[facetName] || facetName;
  };

  const facetDisplayName = (facetName) => {
    const names = {
      'question_types': 'Question Type',
      'senators': 'Senator',
      'committees': 'Committee',
      'portfolios': 'Portfolio',
      'agencies': 'Agency',
      'statuses': 'Status',
      'estimates_rounds': 'Estimates Round'
    };
    return names[facetName] || facetName.replace('_', ' ');
  };

  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const toggleMetadataExpansion = (questionId) => {
    setExpandedMetadata(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const handleModeChange = (event, newMode) => {
    if (newMode !== null) {
      setSearchMode(newMode);
    }
  };

  const handleReset = () => {
    setQuery('');
    setForm({
      keywords: '',
      senator: '',
      committee: '',
      portfolio: '',
      agency: '',
      estimatesRound: '',
      hearingDateFrom: '',
      hearingDateTo: '',
      questionNumber: '',
      questionStatus: 'all',
      portfolioQuestionNumber: ''
    });
    setActiveFilters({});
    setResults(null);
    setPage(1);
    setSearchParams({});
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Senate Estimates Questions</Typography>
        <ToggleButtonGroup
          value={searchMode}
          exclusive
          onChange={handleModeChange}
          size="small"
        >
          <ToggleButton value="simple">
            <SearchIcon sx={{ mr: 1 }} /> Simple
          </ToggleButton>
          <ToggleButton value="advanced">
            <TuneIcon sx={{ mr: 1 }} /> Advanced
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simple Search */}
      <Collapse in={searchMode === 'simple'}>
        <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              placeholder="Try: senator:Watt AND portfolio:Health"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              variant="outlined"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
              Reset
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Examples:
            </Typography>
            {examples.map((ex, i) => (
              <Chip
                key={i}
                label={ex}
                size="small"
                onClick={() => setQuery(ex)}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        </Paper>
      </Collapse>

      {/* Advanced Search */}
      <Collapse in={searchMode === 'advanced'}>
        <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords"
                value={form.keywords}
                onChange={handleChange('keywords')}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search across all fields"
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.senators}
                value={form.senator}
                onChange={handleAutocompleteChange('senator')}
                renderInput={(params) => <TextField {...params} label="Senator" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.committees}
                value={form.committee}
                onChange={handleAutocompleteChange('committee')}
                renderInput={(params) => <TextField {...params} label="Committee" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.portfolios}
                value={form.portfolio}
                onChange={handleAutocompleteChange('portfolio')}
                renderInput={(params) => <TextField {...params} label="Portfolio" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.agencies}
                value={form.agency}
                onChange={handleAutocompleteChange('agency')}
                renderInput={(params) => <TextField {...params} label="Agency" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableOptions.estimatesRounds}
                value={form.estimatesRound}
                onChange={handleAutocompleteChange('estimatesRound')}
                renderInput={(params) => <TextField {...params} label="Estimates Round" />}
                freeSolo
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Hearing Date From"
                type="date"
                value={form.hearingDateFrom}
                onChange={handleChange('hearingDateFrom')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Hearing Date To"
                type="date"
                value={form.hearingDateTo}
                onChange={handleChange('hearingDateTo')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Question Number"
                type="number"
                value={form.questionNumber}
                onChange={handleChange('questionNumber')}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.questionStatus}
                  onChange={handleChange('questionStatus')}
                  label="Status"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="answered">Answered</MenuItem>
                  <MenuItem value="unanswered">Unanswered</MenuItem>
                  <MenuItem value="overdue">Overdue</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Portfolio Q#"
                value={form.portfolioQuestionNumber}
                onChange={handleChange('portfolioQuestionNumber')}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Search
                </Button>
                <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2 }}>
                  Reset
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Query: <code>{buildAdvancedQuery() || '(empty)'}</code>
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {results && (
        <Grid container spacing={3}>
          {/* Facets Panel */}
          {results.facets && Object.keys(results.facets).length > 0 && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3, position: 'sticky', top: 16 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Filter Results
                  </Typography>
                  {Object.keys(activeFilters).length > 0 && (
                    <Button size="small" onClick={clearAllFilters} sx={{ minWidth: 'auto', fontSize: '0.75rem' }}>
                      Clear All
                    </Button>
                  )}
                </Box>

                {/* Active Filters */}
                {Object.keys(activeFilters).length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Active Filters:
                    </Typography>
                    {Object.entries(activeFilters).map(([field, values]) =>
                      values.map((value) => (
                        <Chip
                          key={`${field}-${value}`}
                          label={value}
                          size="small"
                          onDelete={() => removeFilter(field, value)}
                          sx={{ mr: 0.5, mb: 0.5, fontSize: '0.75rem' }}
                        />
                      ))
                    )}
                  </Box>
                )}

                {sortFacetsBooleansFirst(['senators', 'portfolios', 'committees', 'agencies', 'statuses', 'estimates_rounds'], results.facets).map((facetName) => (
                  results.facets[facetName] && results.facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={facetName === 'senators' || facetName === 'portfolios'}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          {facetDisplayName(facetName)}
                          {activeFilters[facetToField(facetName)]?.length > 0 && (
                            <Badge
                              badgeContent={activeFilters[facetToField(facetName)].length}
                              color="primary"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0, maxHeight: 200, overflow: 'auto' }}>
                          {results.facets[facetName].slice(0, 15).map((facet) => (
                            <ListItem
                              key={facet.value}
                              button
                              onClick={() => handleFacetClick(facetToField(facetName), facet.value)}
                              sx={{ px: 0, py: 0.5 }}
                            >
                              <Checkbox
                                edge="start"
                                checked={isFilterActive(facetToField(facetName), facet.value)}
                                size="small"
                                sx={{ py: 0 }}
                              />
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {facet.value || '(empty)'}
                                    </Typography>
                                    <Chip
                                      label={facet.count}
                                      size="small"
                                      sx={{ height: 20, fontSize: '0.75rem' }}
                                    />
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          {/* Results Panel */}
          <Grid item xs={12} md={results.facets && Object.keys(results.facets).length > 0 ? 9 : 12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Found {results.total?.toLocaleString()} results ({results.took_ms}ms)
                </Typography>
                <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                <ExportCSVButton
                  dataType="questions"
                  query={query || '*'}
                  totalResults={results.total}
                />
              </Box>
              {results.total > perPage && (
                <Pagination
                  count={Math.ceil(results.total / perPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              )}
            </Box>

            <List>
            {results.hits.map((hit, index) => (
              <React.Fragment key={hit.id}>
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemText
                    primary={
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="resultTitle">
                            {hit.estimates_round} - {hit.committee} - {hit.asked_of || hit.agency} - Q{hit.question_number}
                          </Typography>
                          {hit.overdue && hit.overdue.toLowerCase() === 'yes' && (
                            <Chip label="Overdue" color="error" size="small" />
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                          {[
                            hit.senator && `Senator: ${hit.senator}`,
                            hit.portfolio && `Portfolio: ${hit.portfolio}`,
                            hit.hearing_date && `Hearing Date: ${hit.hearing_date}`,
                            hit.status && `Status: ${hit.status}`
                          ].filter(Boolean).join(' · ')}
                        </Typography>
                        {hit.topic && (
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                            {hit.topic}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {hit.question_text && (
                          <Typography className="ps-snippet" variant="body2" sx={{ mb: 1.5, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                            {expandedQuestions[hit.id] ? hit.question_text : hit.question_text.substring(0, 300)}
                            {!expandedQuestions[hit.id] && hit.question_text.length > 300 && '... '}
                            {hit.question_text.length > 300 && (
                              <Button
                                size="small"
                                onClick={() => toggleQuestionExpansion(hit.id)}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.875rem',
                                  minWidth: 'auto',
                                  p: 0,
                                  ml: 0.5,
                                  verticalAlign: 'baseline'
                                }}
                              >
                                {expandedQuestions[hit.id] ? 'Show less' : 'Show more'}
                              </Button>
                            )}
                          </Typography>
                        )}

                        {expandedMetadata[hit.id] && (
                          <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 0.5, mb: 1, bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                            {hit.question_number && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Question Number:</Typography>
                                <Typography variant="body2">{hit.question_number}</Typography>
                              </>
                            )}
                            {hit.senator && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Senator:</Typography>
                                <Typography variant="body2">{hit.senator}</Typography>
                              </>
                            )}
                            {hit.committee && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Committee:</Typography>
                                <Typography variant="body2">{hit.committee}</Typography>
                              </>
                            )}
                            {hit.estimates_round && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Estimates Round:</Typography>
                                <Typography variant="body2">{hit.estimates_round}</Typography>
                              </>
                            )}
                            {hit.portfolio && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Portfolio:</Typography>
                                <Typography variant="body2">{hit.portfolio}</Typography>
                              </>
                            )}
                            {hit.topic && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Broad Topic:</Typography>
                                <Typography variant="body2">{hit.topic}</Typography>
                              </>
                            )}
                            {(hit.asked_of || hit.agency) && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Agency Asked:</Typography>
                                <Typography variant="body2">{hit.asked_of || hit.agency}</Typography>
                              </>
                            )}
                            {hit.status && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Status:</Typography>
                                <Typography variant="body2">{hit.status}</Typography>
                              </>
                            )}
                            {hit.hearing_date && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Hearing Date:</Typography>
                                <Typography variant="body2">{hit.hearing_date}</Typography>
                              </>
                            )}
                            {hit.overdue && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Overdue:</Typography>
                                <Typography variant="body2">{hit.overdue}</Typography>
                              </>
                            )}
                            {hit.portfolio_question_number && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Portfolio Q#:</Typography>
                                <Typography variant="body2">{hit.portfolio_question_number}</Typography>
                              </>
                            )}
                            {hit.proof_hansard_page && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Hansard Page:</Typography>
                                <Typography variant="body2">{hit.proof_hansard_page}</Typography>
                              </>
                            )}
                            {hit.indexed_at && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Indexed At:</Typography>
                                <Typography variant="body2">{new Date(hit.indexed_at).toLocaleString()}</Typography>
                              </>
                            )}
                            {hit.question_pdf_url && (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Answer PDF URL:</Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{hit.question_pdf_url}</Typography>
                              </>
                            )}
                          </Box>
                        )}

                        <Box sx={{ mb: 1 }}>
                          <Button
                            size="small"
                            onClick={() => toggleMetadataExpansion(hit.id)}
                            sx={{ textTransform: 'none', fontSize: '0.875rem', p: 0, minWidth: 'auto' }}
                          >
                            {expandedMetadata[hit.id] ? 'Hide metadata' : 'Show all metadata'}
                          </Button>
                        </Box>
                        {hit.question_pdf_url && (
                          <Link
                            href={hit.question_pdf_url}
                            target="_blank"
                            rel="noopener"
                            sx={{ fontWeight: 500 }}
                          >
                            Download answer
                          </Link>
                        )}
                      </>
                    }
                  />
                </ListItem>
                {index < results.hits.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            </List>

            {results.total > perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(results.total / perPage)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
